import { useEffect, useRef } from "react";

export const FILTER_ANCHOR_ATTR = "data-filter-anchor";

export type FilterPanelPosition = { x: number; y: number };

export function computeFilterPanelPositionFromRect(
  rect: DOMRect | DOMRectReadOnly,
): FilterPanelPosition {
  const viewportWidth = window.innerWidth;
  const filterPanelWidth = 250;
  let x = rect.left + 24;
  let y = rect.bottom + 10;

  if (x + filterPanelWidth + 46 > viewportWidth) {
    x = viewportWidth - filterPanelWidth - 46;
  }

  x = Math.max(10, x);
  y = Math.max(10, y);

  return { x, y };
}

function escapeForAttributeSelector(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function getFilterAnchorSelector(columnId: string | number): string {
  return `[${FILTER_ANCHOR_ATTR}="${escapeForAttributeSelector(
    String(columnId),
  )}"]`;
}

export function useSyncFilterPanelPositionWhileOpen(options: {
  open: boolean;
  columnId: string | number;
  setFilterInfo: (updater: (prev: any) => any) => void;
}): void {
  const { open, columnId, setFilterInfo } = options;
  const lastRoundedRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!open) {
      lastRoundedRef.current = null;
      return;
    }

    const col = String(columnId);
    let rafId = 0;

    const tick = () => {
      const anchor = document.querySelector(
        getFilterAnchorSelector(col),
      ) as HTMLElement | null;

      if (anchor) {
        const pos = computeFilterPanelPositionFromRect(
          anchor.getBoundingClientRect(),
        );
        const rx = Math.round(pos.x);
        const ry = Math.round(pos.y);

        if (
          !lastRoundedRef.current ||
          lastRoundedRef.current.x !== rx ||
          lastRoundedRef.current.y !== ry
        ) {
          lastRoundedRef.current = { x: rx, y: ry };
          setFilterInfo((prev: any) => {
            if (!prev?.open || String(prev.columnId) !== col) {
              return prev;
            }
            const px = prev.position?.x;
            const py = prev.position?.y;
            if (
              px != null &&
              py != null &&
              Math.round(px) === rx &&
              Math.round(py) === ry
            ) {
              return prev;
            }
            return { ...prev, position: { x: pos.x, y: pos.y } };
          });
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [open, columnId, setFilterInfo]);
}
